/**
 * Structured step edits for the flowchart.
 *
 * The flowchart is a projection of `WorkflowPlan`; clicking a node edits the underlying step and
 * then re-projects. Only the fields the finalize validator understands are touched, layout hints
 * (`x`/`y`/`group`) are preserved, and structural nodes (branch/loop/sub_template) are refused
 * here — those are graph-shape changes and belong to the chat/proposal path.
 */

import type { FlowchartHint, WorkflowPlan, WorkflowStep } from "./types";

export interface StepEdit {
  /** Display hint only; never part of the executable plan. */
  label?: string;
  url?: string;
  testIds?: string[];
  /** First extract capture (additional captures are preserved). */
  output?: string;
  source?: string;
  artifactName?: string;
  reason?: string;
  auth?: boolean;
  action?: string;
  maxAttempts?: number;
}

export interface StepPatchResult {
  plan: WorkflowPlan;
  flowchart: FlowchartHint[];
  changed: boolean;
  error?: string;
  /** Fields the editor should not have offered for this kind. */
  ignored?: string[];
}

const STRUCTURAL_KINDS = new Set(["branch", "loop", "sub_template"]);

function mergeHint(
  hints: FlowchartHint[],
  stepId: string,
  label: string | undefined,
): FlowchartHint[] {
  if (label === undefined) return hints;
  const trimmed = label.trim();
  if (!trimmed) return hints;
  const existing = hints.find((hint) => hint.step_id === stepId);
  if (existing) {
    return hints.map((hint) =>
      hint.step_id === stepId ? { ...hint, label: trimmed } : hint,
    );
  }
  return [...hints, { step_id: stepId, label: trimmed }];
}

export function patchWorkflowStep(
  plan: WorkflowPlan,
  flowchart: FlowchartHint[],
  stepId: string,
  edit: StepEdit,
): StepPatchResult {
  const next: WorkflowPlan = structuredClone(plan);
  const step = next.steps.find((candidate) => candidate.step_id === stepId);
  if (!step) {
    return { plan, flowchart, changed: false, error: "step not found in this plan" };
  }
  const ignored: string[] = [];
  const before = JSON.stringify(step);

  if (edit.maxAttempts !== undefined) {
    if (!Number.isInteger(edit.maxAttempts) || edit.maxAttempts < 1) {
      return {
        plan,
        flowchart,
        changed: false,
        error: "max attempts must be a whole number of at least 1",
      };
    }
    step.max_attempts = edit.maxAttempts;
  }

  if (edit.url !== undefined) {
    if (step.kind !== "navigate") ignored.push("url");
    else {
      const url = edit.url.trim();
      if (!url || /\s/.test(url)) {
        return { plan, flowchart, changed: false, error: "enter a URL without spaces" };
      }
      step.url = url;
    }
  }

  if (edit.testIds !== undefined) {
    if (step.kind !== "verify") ignored.push("testIds");
    else {
      const testIds = edit.testIds.map((id) => id.trim()).filter(Boolean);
      if (testIds.length === 0) {
        return {
          plan,
          flowchart,
          changed: false,
          error: "a verify step needs at least one verification test id",
        };
      }
      step.test_ids = testIds;
    }
  }

  if (edit.output !== undefined || edit.source !== undefined) {
    if (step.kind !== "extract") ignored.push("captures");
    else {
      const captures = step.captures ?? [];
      const first = captures[0] ?? { output: "", source: "" };
      const output = (edit.output ?? first.output).trim();
      const source = (edit.source ?? first.source).trim();
      if (!output || !source) {
        return {
          plan,
          flowchart,
          changed: false,
          error: "an extract capture needs both an output name and a source",
        };
      }
      step.captures = [{ output, source }, ...captures.slice(1)];
    }
  }

  if (edit.artifactName !== undefined) {
    if (step.kind !== "download") ignored.push("artifactName");
    else {
      const artifactName = edit.artifactName.trim();
      if (!artifactName) {
        return { plan, flowchart, changed: false, error: "a download needs an artifact name" };
      }
      step.artifact_name = artifactName;
    }
  }

  if (edit.reason !== undefined) {
    if (step.kind !== "hitl_request") ignored.push("reason");
    else {
      const reason = edit.reason.trim();
      if (!reason) {
        return { plan, flowchart, changed: false, error: "a HITL request needs a reason" };
      }
      step.reason = reason;
    }
  }

  if (edit.auth !== undefined) {
    if (step.kind !== "hitl_request") ignored.push("auth");
    else step.auth = edit.auth;
  }

  if (edit.action !== undefined) {
    if (step.kind !== "form_submit") ignored.push("action");
    else {
      const action = edit.action.trim();
      if (action) step.action = action;
      else delete step.action;
    }
  }

  if (STRUCTURAL_KINDS.has(step.kind) && Object.keys(edit).some((key) => key !== "label" && key !== "maxAttempts")) {
    return {
      plan,
      flowchart,
      changed: false,
      error: "branch, loop and sub-template shapes are edited through the builder chat",
    };
  }

  const stepChanged = JSON.stringify(step) !== before;
  const nextHints = mergeHint(flowchart, stepId, edit.label);
  const hintChanged = nextHints !== flowchart;
  return {
    plan: stepChanged ? next : plan,
    flowchart: nextHints,
    changed: stepChanged || hintChanged,
    ignored: ignored.length ? ignored : undefined,
  };
}

/** Convenience for the editor: the label/kind a row should display. */
export function describeStep(step: WorkflowStep, hints: FlowchartHint[]): string {
  return hints.find((hint) => hint.step_id === step.step_id)?.label ?? step.kind;
}
